import React, { useMemo, memo } from "react";
import { Bug, Loader2 } from "lucide-react";
import { CopyButton } from "~/components/assistant/CopyButton";
import { FeedbackButtons } from "~/components/assistant/FeedbackButtons";
import { MessageContent } from "~/components/assistant/MessageContent";
import type { Message, MessageGroup, MessageBinaryFeedback } from "~/components/assistant/types";
import { cn } from "~/lib/utils";

function TraceId({ traceId, debugMode }: { traceId?: string; debugMode: boolean }) {
  if (!debugMode || !traceId) {
    return null;
  }

  return (
    <CopyButton
      text={traceId}
      icon={Bug}
      tooltipText={`Copy Trace ID: ${traceId}`}
      variant="light"
      size="sm"
    />
  );
}

export interface ConversationViewerProps {
  /**
   * The messages to display
   */
  messages: Message[];
  /**
   * Whether the assistant is currently loading/streaming
   */
  isLoading: boolean;
  /**
   * Whether conversation history is being loaded
   */
  isLoadingHistory?: boolean;
  /**
   * The current conversation ID
   */
  currentConversationId: string | null;
  /**
   * Whether a new conversation is being created
   */
  isCreatingConversation: boolean;
  /**
   * Whether debug mode is enabled (shows trace IDs)
   */
  debugMode: boolean;
  /**
   * Agent message minimum height configuration
   */
  agentMinHeight: { id: string | null; height: number };
  /**
   * Ref handler for message elements (for scroll management)
   */
  handleMessageRef: (id: string, el: HTMLDivElement | null) => void;
  /**
   * Ref for the scroll container
   */
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Ref for the content container
   */
  contentRef: React.RefObject<HTMLDivElement | null>;
  /**
   * The title of the context (e.g., dataset name)
   */
  contextTitle: string;
  /**
   * Optional custom title for the empty state
   */
  emptyStateTitle?: string;
  /**
   * Optional custom description for the empty state
   */
  emptyStateDescription?: string;
  /**
   * Callback for handling message feedback (thumbs up/down)
   */
  onFeedback?: (messageId: string, feedback: MessageBinaryFeedback | null) => Promise<void>;
}

export const ConversationViewer = memo(function ConversationViewer({
  messages,
  isLoading,
  isLoadingHistory = false,
  currentConversationId,
  isCreatingConversation,
  debugMode,
  agentMinHeight,
  handleMessageRef,
  scrollContainerRef,
  contentRef,
  contextTitle,
  emptyStateTitle,
  emptyStateDescription,
  onFeedback,
}: ConversationViewerProps) {
  // Group messages for display
  const messageGroups = useMemo(
    () =>
      messages.reduce<MessageGroup[]>((groups, message) => {
        if (message.kind === "user_chat") {
          // Start new group with user message
          groups.push({
            id: `group-${message.id}`,
            user: message,
            agent: [],
          });
        } else if (message.kind === "model_chat" && groups.length > 0) {
          // Add agent message to most recent group
          groups[groups.length - 1].agent.push(message);
        }
        return groups;
      }, []),
    [messages]
  );

  return (
    <div className="flex-1 min-h-0 relative w-full">
      <div
        className={cn(
          "h-full overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300",
          "[mask-image:linear-gradient(black_calc(100%-24px),transparent_100%),linear-gradient(black,black)]",
          "[mask-size:calc(100%-16px)_100%,16px_100%]",
          "[mask-position:0px_0px,100%_0px]",
          "[mask-repeat:no-repeat,no-repeat]",
          isLoading ? "scroll-auto" : "scroll-smooth"
        )}
        ref={scrollContainerRef}
      >
        <div
          ref={contentRef}
          className="flex flex-col gap-2 max-w-[900px] mx-auto items-start justify-start pt-0 px-4 pb-4 text-foreground text-left min-h-full"
        >
          {messages.length === 0 && (isLoadingHistory || isLoading || isCreatingConversation) ? (
            <div className="flex flex-col gap-2 w-full flex-1 justify-center items-center">
              <Loader2 className="size-8 text-gray-400 animate-spin" />
            </div>
          ) : currentConversationId &&
            messages.length === 0 &&
            !isLoadingHistory &&
            !isLoading &&
            !isCreatingConversation ? (
            <div className="flex flex-col gap-2 w-full flex-1 justify-center">
              <div className="flex flex-col font-normal items-center justify-center relative shrink-0 text-sm">
                <div className="flex flex-col font-semibold justify-center relative shrink-0 text-[20px]">
                  <p className="block leading-[32px]">
                    {emptyStateTitle || `Explore this ${contextTitle}`}
                  </p>
                </div>
                <p className="block text-center leading-[16px] my-2 text-gray-600">
                  {emptyStateDescription ||
                    "Ask questions, generate tables or charts, and compare data across tables."}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col w-full justify-start space-y-4">
              {messageGroups.map((group) => (
                <div key={group.id} className="relative">
                  {/* User message - sticky positioned */}
                  <div
                    ref={(el) => handleMessageRef(group.user.id, el)}
                    className="sticky top-0 z-10 bg-white"
                  >
                    <div
                      data-kind={group.user.kind}
                      className={cn(
                        "px-3 py-2 rounded-lg select-text bg-gray-100 border-1 border-gray-300 grid w-full items-start",
                        {
                          "grid-cols-[1fr_30px] gap-1": debugMode,
                          "grid-cols-[1fr]": !debugMode,
                        }
                      )}
                    >
                      <MessageContent message={group.user} debugMode={debugMode} />
                      <TraceId traceId={group.user.traceId} debugMode={debugMode} />
                    </div>
                  </div>

                  {/* Agent messages */}
                  {group.agent.map((message) => {
                    // Show feedback buttons for model_chat messages from chat history
                    const showFeedback =
                      onFeedback &&
                      message.kind === "model_chat" &&
                      !message.streaming &&
                      !message.error &&
                      !message.warning &&
                      // Only show for messages with backend IDs (UUID format from chat history)
                      message.id.includes("-") &&
                      !message.id.startsWith("agent-");

                    return (
                      <div
                        key={message.id}
                        ref={(el) => handleMessageRef(message.id, el)}
                        className="full-w select-none"
                      >
                        <div
                          data-kind={message.kind}
                          className="px-1.5 select-text flex-1"
                          style={{
                            minHeight:
                              agentMinHeight.id === message.id
                                ? `${agentMinHeight.height}px`
                                : undefined,
                          }}
                        >
                          <MessageContent message={message} debugMode={debugMode} />
                          {showFeedback && (
                            <FeedbackButtons message={message} onFeedback={onFeedback} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
